import { isLeft } from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import { NonEmptyString } from 'io-ts-types';
import { TableColumnProperties } from './tableSettings';

const TableSettingsCodec = t.array(
    t.type({
        column: NonEmptyString,
        visible: t.boolean,
    }),
);

const STORAGE_KEY_PREFIX = 'tableSettings.';

export const saveTableSettings = (tableId: string, settings: TableColumnProperties<string>) => {
    localStorage.setItem(STORAGE_KEY_PREFIX + tableId, JSON.stringify(settings));
};

export const loadTableSettings = <TableColumnEnum extends string>(
    tableId: string,
    defaultSettings: TableColumnProperties<TableColumnEnum>,
): TableColumnProperties<TableColumnEnum> => {
    const storedValue = localStorage.getItem(STORAGE_KEY_PREFIX + tableId);
    if (!storedValue) {
        return defaultSettings;
    }

    let parsedValue: unknown;
    try {
        parsedValue = JSON.parse(storedValue);
    } catch (error) {
        console.error('Failed to parse table settings', error);
        return defaultSettings;
    }

    const validatedValue = TableSettingsCodec.decode(parsedValue);
    if (isLeft(validatedValue)) {
        console.error('Failed to validate table settings', validatedValue.left);
        return defaultSettings;
    }

    const withoutUnknownColumns = validatedValue.right
        .map(({ column, visible }) => {
            const foundDefaultSetting = defaultSettings.find((def) => def.column === (column as string));
            return foundDefaultSetting ? { ...foundDefaultSetting, visible: Boolean(visible) } : undefined;
        })
        .filter((found) => found !== undefined);

    const missingColumns = defaultSettings.filter(
        (def) => !withoutUnknownColumns.find(({ column }) => def.column === column),
    );

    return [...withoutUnknownColumns, ...missingColumns];
};
